import React, { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import SubPage, { PageAction, PageActions } from '#/common/components/SubPage';
import useWorkOrderQuery from '../hooks/useWorkOrderQuery';
import WorkOrderForm from '../components/WorkOrderForm';
import getFormTitle from '#/common/utils/getFormTitle';
import { Tab } from '#/common/components/SubPage/SubPageProps';
import Expenses from '#/modules/expense/components/Expenses';
import If from '#/common/components/If';
import fields from '../components/WorkOrderForm/fields';
import useWorkOrderUpdate from '../hooks/useWorkOrderUpdate';
import { SubmitWorkOrder } from '../utils/types';
import { WorkOrderDetails } from '#/graphql/types';
import useWorkOrderDelete from '../hooks/useWorkOrderDelete';
import useWorkOrderRelatedCounts from '../hooks/useWorkOrderRelatedCountsQuery';
import useWorkOrderSeeAsUser from '../hooks/useWorkOrderSeeAsUser';

export interface WorkOrderParams {
  readonly id?: string;
}
export type WorkOrderTabs = 'details' | 'expenses';

export const defaultTab: WorkOrderTabs = 'details';

export interface WorkOrderProps {
  readonly tab?: WorkOrderTabs;
}

const tabs: Tab<WorkOrderTabs>[] = [
  {
    key: 'details',
    text: 'Details',
    type: 'form',
  },
  {
    key: 'expenses',
    text: 'Expenses',
    type: 'grid',
  },
];

const expensesOmitCols = ['workOrder.title'];

const WorkOrderPage: React.FC<WorkOrderProps> = ({ tab }) => {
  const { id } = useParams() as WorkOrderParams;
  const workOrderQuery = useWorkOrderQuery(id);
  const workOrder = workOrderQuery.data?.getWorkOrder?.data;

  const { seeWorkOrderAsUser } = useWorkOrderSeeAsUser();

  useEffect(() => {
    if (id) seeWorkOrderAsUser(id);
  }, [id, seeWorkOrderAsUser]);

  const workOrderTransformed = useMemo(() => {
    const res = { ...workOrder } as WorkOrderDetails;

    try {
      res.duration =
        workOrder?.duration && (Number(workOrder?.duration?.split(':')[0]) / 24).toString();
    } catch (e) {
      res.duration = null;
    }

    return res;
  }, [workOrder]);

  const { data: workOrderRelatedCounts } = useWorkOrderRelatedCounts(id ?? '');
  const tenantId = workOrderTransformed?.tenantId;

  const workOrderNumber = getFormTitle(id, workOrderTransformed?.displayNumber, 'Work Order');
  const title = getFormTitle(id, workOrderNumber, 'Work Order');

  const workOrderTabs = useMemo(
    () =>
      tabs.map((t) => {
        if (t.key === 'expenses')
          return {
            ...t,
            count: workOrderRelatedCounts?.expenses ?? 0,
          };
        return t;
      }),
    [workOrderRelatedCounts, id, tenantId],
  );

  const { updateWorkOrder } = useWorkOrderUpdate();
  const submit = useCallback<SubmitWorkOrder>(
    async (workOrder: WorkOrderDetails) => {
      if (!id) {
        return undefined;
      }

      const { data, errors } = (await updateWorkOrder(id, workOrder)) as {
        data?: WorkOrderDetails;
        errors?: string[];
      };

      seeWorkOrderAsUser(id);

      return {
        data: data ?? undefined,
        errors: errors ?? undefined,
      };
    },
    [id, updateWorkOrder, seeWorkOrderAsUser],
  );

  // Delete Operation
  const { deleteWorkOrder: delWorkOrder } = useWorkOrderDelete();
  const deleteWorkOrder = useCallback(() => {
    if (!id) {
      return undefined;
    }

    return delWorkOrder(id);
  }, [delWorkOrder, id]);

  const actions = useMemo<PageAction[]>(
    () => [
      {
        id: `new-expense`,
        alwaysShow: true,
        type: 'button',
        props: {
          visible: workOrderTransformed?.state !== 'closed',
          type: 'submit',
          icon: 'add',
          label: `Create New Expense`,
          responsive: true,
          path: `/expenses/new${workOrder?.id ? `?workOrderId=${workOrder.id}` : ''}`,
        },
      },
    ],
    [workOrder?.id, workOrderTransformed?.state],
  );

  return (
    <SubPage
      type={!tab || tab === 'details' ? 'form' : 'list'}
      selectedTab={tab || 'details'}
      tabs={workOrderTabs}
      title={title}
      isLoading={workOrderQuery.loading}
      showGoBackButton>
      <If condition={tab === 'details' || tab === undefined}>
        {workOrderTransformed && (
          <WorkOrderForm
            key={`work-order-form-${workOrderTransformed.id}-${workOrderTransformed.updatedAt}`}
            title={title}
            workOrder={workOrderTransformed}
            fields={fields}
            deleteWorkOrder={deleteWorkOrder}
            submit={submit}
          />
        )}
      </If>
      <If condition={tab === 'expenses'}>
        {
          <>
            <PageActions actions={actions} />
            <Expenses
              omitColumns={expensesOmitCols}
              permanentFilters={id ? [['workOrderId', '=', id]] : undefined}
            />
          </>
        }
      </If>
    </SubPage>
  );
};

export default React.memo(WorkOrderPage);
